// BLOCK_SCHEMAS — the admin's field vocabulary for CMS blocks (motiflink §2.4).
//
// This map is CODE-OWNED, deliberately. motiflink is explicit that a DB-driven
// `block_types` table was tried and abandoned: the renderer switch in
// apps/web/src/components/BlockRenderer.astro and the Zod schemas in
// packages/zod-schemas/src/blocks.ts are already code, so a third source of
// truth in the database can only ever drift out of sync with them.
//
// Adding a block type is therefore a four-line ritual (see the header of
// packages/zod-schemas/src/blocks.ts): DB enum → Zod schema → Astro renderer →
// an entry here.
//
// ── The content/translations split ──────────────────────────────────────────
//
// `bilingual: true` means the field is COPY: the Arabic value lives in
// `content[key]` and the English override in `translations.en[key]`.
//
// `bilingual` absent/false means the value is LANGUAGE-NEUTRAL — hrefs, icon
// slugs, accents, glyphs, character slugs, price digits, avatar URLs, column
// counts, tones, widths, highlight flags. Those live ONLY in `content`; the
// API's overlay schema (`blockTranslationsByType`, built from NEUTRAL_KEYS)
// REJECTS an overlay that carries them. Marking one bilingual here would ship
// an editor that saves 400s.
//
// Field labels are English-only by owner ruling (CLAUDE.md §5: admin chrome is
// not translated).

import { BLOCK_TYPES, type BlockType } from '@fajrarabi/zod-schemas/blocks';

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'number'
  | 'boolean'
  | 'color'
  | 'icon'
  | 'image'
  | 'select'
  | 'list';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Copy: AR in `content`, EN in `translations.en`. Never set on neutral values. */
  bilingual?: boolean;
  /** Editor affordance only — the API is the authority on what's required. */
  required?: boolean;
  help?: string;
  placeholder?: string;
  /** `kind: 'list'` only — the shape of one item. */
  itemFields?: FieldSpec[];
  /** `kind: 'list'` only — arity bounds mirrored from the Zod schema. */
  minItems?: number;
  maxItems?: number;
  /**
   * `kind: 'list'` only. A Zod *tuple* (stats_trio, dual_value_cards,
   * testimonial_pair) has fixed arity — the editor hides add/remove so an
   * editor can't produce a payload the API will reject.
   */
  fixedLength?: number;
  /**
   * `kind: 'list'` only. The value is a single nested OBJECT (a `cta`, the
   * `newsletter` form), not an array — the editor reuses the list's
   * itemFields renderer but addresses the leaves without an index segment.
   * Set `optional: true` when the whole object may be null.
   */
  object?: true;
  /** `object: true` only — the group can be removed entirely. */
  optional?: true;
  /** `kind: 'select'` only. */
  options?: FieldOption[];
  /** `kind: 'number'` only. */
  min?: number;
  max?: number;
}

export interface BlockSchema {
  label: string;
  description: string;
  contentFields: FieldSpec[];
  /**
   * Layout/behaviour knobs stored in `settings` rather than `content`. The
   * current ten block types keep every knob inside their Zod content schema
   * (`columns`, `tone`, `width`), so these lists are empty — the plumbing is
   * here because motiflink §2.1 gives every block a `settings` JSONB and new
   * types are expected to use it.
   */
  settingsFields: FieldSpec[];
}

// ─── Shared field fragments ─────────────────────────────────────────────────

const eyebrow: FieldSpec = {
  key: 'eyebrow',
  label: 'Eyebrow',
  kind: 'text',
  bilingual: true,
  help: 'Small line above the heading. Optional.',
};

const title = (required: boolean): FieldSpec => ({
  key: 'title',
  label: 'Title',
  kind: 'text',
  bilingual: true,
  required,
});

const body = (required: boolean): FieldSpec => ({
  key: 'body',
  label: 'Body',
  kind: 'textarea',
  bilingual: true,
  required,
});

/** `ctaShape` — label is copy, href and variant are neutral. */
const CTA_FIELDS: FieldSpec[] = [
  { key: 'label', label: 'Label', kind: 'text', bilingual: true, required: true },
  {
    key: 'href',
    label: 'Link',
    kind: 'text',
    required: true,
    placeholder: '/pricing, #signup, {app}/signup, https://…',
    help: 'Must start with / # {app} http:// https:// mailto: or tel:. {app} and {locale} are resolved per visitor.',
  },
  {
    key: 'variant',
    label: 'Style',
    kind: 'select',
    options: [
      { value: 'primary', label: 'Primary' },
      { value: 'secondary', label: 'Secondary' },
      { value: 'ghost', label: 'Ghost' },
    ],
  },
];

/**
 * A CTA is a nested OBJECT, not an array. `object: true` reuses the list
 * renderer's itemFields walk while addressing `content.cta.label` rather than
 * `content.cta[0].label`.
 */
const ctaField = (key: string, label: string, optional?: true, help?: string): FieldSpec => ({
  key,
  label,
  kind: 'list',
  object: true,
  itemFields: CTA_FIELDS,
  ...(optional ? { optional } : {}),
  ...(help ? { help } : {}),
});

const CHARACTER_OPTIONS: FieldOption[] = [
  { value: '', label: 'None' },
  { value: 'basil', label: 'Basil' },
  { value: 'ward', label: 'Ward' },
];

const ICON_OPTIONS: FieldOption[] = [
  'sparkle',
  'book',
  'mosque',
  'globe',
  'leaf',
  'heart',
  'star',
  'sun',
  'compass',
  'puzzle',
].map((value) => ({ value, label: value[0]!.toUpperCase() + value.slice(1) }));

// ─── The map ────────────────────────────────────────────────────────────────

export const BLOCK_SCHEMAS: Record<BlockType, BlockSchema> = {
  hero_split: {
    label: 'Hero',
    description: 'Full-width opener: heading, supporting copy, up to three CTAs, optional mascots.',
    contentFields: [
      eyebrow,
      title(true),
      { key: 'subtitle', label: 'Subtitle', kind: 'textarea', bilingual: true },
      {
        key: 'ctas',
        label: 'Call-to-action buttons',
        kind: 'list',
        minItems: 1,
        maxItems: 3,
        itemFields: CTA_FIELDS,
      },
      {
        key: 'start_character',
        label: 'Character (start side)',
        kind: 'select',
        options: CHARACTER_OPTIONS,
      },
      {
        key: 'end_character',
        label: 'Character (end side)',
        kind: 'select',
        options: CHARACTER_OPTIONS,
      },
    ],
    settingsFields: [],
  },

  stats_trio: {
    label: 'Stats trio',
    description: 'Exactly three headline numbers with captions.',
    contentFields: [
      eyebrow,
      title(false),
      {
        key: 'stats',
        label: 'Stats',
        kind: 'list',
        fixedLength: 3,
        help: 'Exactly three. The number itself is language-neutral; only its caption is translated.',
        itemFields: [
          {
            key: 'value',
            label: 'Value',
            kind: 'text',
            required: true,
            placeholder: '12',
            help: 'Shown verbatim. Max 20 characters.',
          },
          { key: 'label', label: 'Caption', kind: 'text', bilingual: true, required: true },
        ],
      },
    ],
    settingsFields: [],
  },

  dual_value_cards: {
    label: 'Dual value cards',
    description: 'Two side-by-side cards — the parents/schools split on the homepage.',
    contentFields: [
      eyebrow,
      title(false),
      body(false),
      {
        key: 'cards',
        label: 'Cards',
        kind: 'list',
        fixedLength: 2,
        help: 'Exactly two.',
        itemFields: [
          { key: 'eyebrow', label: 'Eyebrow', kind: 'text', bilingual: true },
          { key: 'title', label: 'Title', kind: 'text', bilingual: true, required: true },
          { key: 'body', label: 'Body', kind: 'textarea', bilingual: true, required: true },
          {
            key: 'bullets',
            label: 'Bullets',
            kind: 'list',
            maxItems: 8,
            itemFields: [
              { key: '', label: 'Bullet', kind: 'text', bilingual: true, required: true },
            ],
          },
          ctaField('cta', 'Call to action'),
          {
            key: 'accent',
            label: 'Accent colour',
            kind: 'color',
            options: [
              { value: 'burgundy', label: 'Burgundy' },
              { value: 'sun', label: 'Sun' },
              { value: 'leaf', label: 'Leaf' },
              { value: 'sky', label: 'Sky' },
            ],
          },
        ],
      },
    ],
    settingsFields: [],
  },

  scrollable_grid_with_character: {
    label: 'Letter grid',
    description: 'Scrollable grid of glyphs (the alphabet strip) with an optional mascot.',
    contentFields: [
      eyebrow,
      title(true),
      body(false),
      {
        key: 'items',
        label: 'Grid items',
        kind: 'list',
        minItems: 4,
        maxItems: 40,
        help: 'Between 4 and 40. The glyph is language-neutral — only its caption is translated.',
        itemFields: [
          {
            key: 'glyph',
            label: 'Glyph',
            kind: 'text',
            required: true,
            placeholder: 'أ',
            help: 'Max 8 characters.',
          },
          { key: 'label', label: 'Caption', kind: 'text', bilingual: true },
        ],
      },
      { key: 'character', label: 'Character', kind: 'select', options: CHARACTER_OPTIONS },
    ],
    settingsFields: [],
  },

  icon_card_grid: {
    label: 'Icon card grid',
    description: 'Two to six feature cards, each with an icon from the bundled set.',
    contentFields: [
      eyebrow,
      title(true),
      body(false),
      {
        key: 'cards',
        label: 'Cards',
        kind: 'list',
        minItems: 2,
        maxItems: 6,
        itemFields: [
          { key: 'icon', label: 'Icon', kind: 'icon', required: true, options: ICON_OPTIONS },
          { key: 'title', label: 'Title', kind: 'text', bilingual: true, required: true },
          { key: 'body', label: 'Body', kind: 'textarea', bilingual: true, required: true },
        ],
      },
      {
        key: 'columns',
        label: 'Columns',
        kind: 'select',
        options: [
          { value: '2', label: '2' },
          { value: '3', label: '3' },
          { value: '4', label: '4' },
        ],
        help: 'Desktop column count.',
      },
      {
        key: 'tone',
        label: 'Background',
        kind: 'select',
        options: [
          { value: 'cream', label: 'Cream' },
          { value: 'white', label: 'White' },
        ],
        help: 'Alternate this between adjacent grids for rhythm.',
      },
    ],
    settingsFields: [],
  },

  pricing_tiers: {
    label: 'Pricing tiers',
    description: 'One to four plan cards plus an optional schools line.',
    contentFields: [
      eyebrow,
      title(true),
      body(false),
      {
        key: 'currency',
        label: 'Currency',
        kind: 'text',
        bilingual: true,
        required: true,
        placeholder: 'ر.ق',
        help: 'Localised (QAR ↔ ر.ق), so it is copy rather than config.',
      },
      {
        key: 'tiers',
        label: 'Tiers',
        kind: 'list',
        minItems: 1,
        maxItems: 4,
        itemFields: [
          { key: 'name', label: 'Name', kind: 'text', bilingual: true, required: true },
          { key: 'tagline', label: 'Tagline', kind: 'text', bilingual: true },
          {
            key: 'price_display',
            label: 'Price',
            kind: 'text',
            required: true,
            placeholder: '39',
            help: 'Digits only, shown verbatim next to the currency. Language-neutral.',
          },
          {
            key: 'period',
            label: 'Period',
            kind: 'text',
            bilingual: true,
            required: true,
            placeholder: 'شهريًا',
          },
          {
            key: 'features',
            label: 'Features',
            kind: 'list',
            maxItems: 10,
            itemFields: [
              { key: '', label: 'Feature', kind: 'text', bilingual: true, required: true },
            ],
          },
          ctaField('cta', 'Call to action'),
          { key: 'highlighted', label: 'Highlight this tier', kind: 'boolean' },
          { key: 'badge', label: 'Badge', kind: 'text', bilingual: true },
        ],
      },
      { key: 'schools_label', label: 'Schools line', kind: 'text', bilingual: true },
      ctaField('schools_cta', 'Schools CTA', true, 'Optional — omit it entirely if unused.'),
    ],
    settingsFields: [],
  },

  testimonial_pair: {
    label: 'Testimonials',
    description: 'Exactly two quotes with attribution.',
    contentFields: [
      eyebrow,
      title(false),
      {
        key: 'items',
        label: 'Quotes',
        kind: 'list',
        fixedLength: 2,
        help: 'Exactly two. No invented social proof — placeholders stay obviously placeholder until real quotes exist.',
        itemFields: [
          { key: 'quote', label: 'Quote', kind: 'textarea', bilingual: true, required: true },
          { key: 'author', label: 'Author', kind: 'text', bilingual: true, required: true },
          { key: 'role', label: 'Role', kind: 'text', bilingual: true },
          { key: 'avatar_url', label: 'Avatar URL', kind: 'image' },
        ],
      },
    ],
    settingsFields: [],
  },

  accordion_faq: {
    label: 'FAQ accordion',
    description: 'Up to twenty expandable question/answer pairs.',
    contentFields: [
      eyebrow,
      title(true),
      body(false),
      {
        key: 'items',
        label: 'Questions',
        kind: 'list',
        minItems: 1,
        maxItems: 20,
        itemFields: [
          { key: 'question', label: 'Question', kind: 'text', bilingual: true, required: true },
          { key: 'answer', label: 'Answer', kind: 'textarea', bilingual: true, required: true },
        ],
      },
    ],
    settingsFields: [],
  },

  footer_cta_newsletter: {
    label: 'Footer CTA + newsletter',
    description: 'Closing band: heading, optional CTA, optional newsletter sign-up form.',
    contentFields: [
      eyebrow,
      title(true),
      body(false),
      ctaField('cta', 'Call to action', true, 'Optional — omit it for newsletter-only.'),
      {
        key: 'newsletter',
        label: 'Newsletter form',
        kind: 'list',
        object: true,
        optional: true,
        help: 'Optional. Omit the form entirely, or fill in all four strings.',
        itemFields: [
          { key: 'heading', label: 'Heading', kind: 'text', bilingual: true, required: true },
          {
            key: 'placeholder',
            label: 'Input placeholder',
            kind: 'text',
            bilingual: true,
            required: true,
          },
          { key: 'submit', label: 'Submit button', kind: 'text', bilingual: true, required: true },
          {
            key: 'success',
            label: 'Success message',
            kind: 'text',
            bilingual: true,
            required: true,
          },
        ],
      },
    ],
    settingsFields: [],
  },

  rich_text: {
    label: 'Rich text',
    description: 'Long-form Markdown — About, Privacy, Terms.',
    contentFields: [
      eyebrow,
      title(false),
      {
        key: 'body',
        label: 'Body',
        kind: 'richtext',
        bilingual: true,
        required: true,
        help: 'Markdown. Rendered to HTML by the site.',
      },
      {
        key: 'width',
        label: 'Content width',
        kind: 'select',
        options: [
          { value: 'prose', label: 'Prose (article)' },
          { value: 'wide', label: 'Wide' },
          { value: 'full', label: 'Full bleed' },
        ],
      },
    ],
    settingsFields: [],
  },

  contact_form: {
    label: 'Contact form',
    description:
      'The form on /contact. Posts to the API and stores the message; only the labels are editable.',
    contentFields: [
      eyebrow,
      title(true),
      { key: 'body', label: 'Intro copy', kind: 'textarea', bilingual: true },
      { key: 'name_label', label: 'Name — label', kind: 'text', bilingual: true, required: true },
      { key: 'name_placeholder', label: 'Name — placeholder', kind: 'text', bilingual: true },
      { key: 'email_label', label: 'Email — label', kind: 'text', bilingual: true, required: true },
      { key: 'email_placeholder', label: 'Email — placeholder', kind: 'text', bilingual: true },
      { key: 'phone_label', label: 'Phone — label', kind: 'text', bilingual: true, required: true },
      { key: 'phone_placeholder', label: 'Phone — placeholder', kind: 'text', bilingual: true },
      { key: 'topic_label', label: 'Topic — label', kind: 'text', bilingual: true, required: true },
      {
        key: 'topic_options',
        label: 'Topic choices',
        kind: 'list',
        minItems: 1,
        maxItems: 8,
        // `value` is NOT bilingual and must stay one of the strings the API's
        // enum accepts — a translated value would be rejected on submit.
        itemFields: [
          { key: 'value', label: 'Value (code)', kind: 'text', required: true },
          { key: 'label', label: 'Label', kind: 'text', bilingual: true, required: true },
        ],
        help: 'Value must be one of: support, billing, schools, feedback, other.',
      },
      {
        key: 'message_label',
        label: 'Message — label',
        kind: 'text',
        bilingual: true,
        required: true,
      },
      { key: 'message_placeholder', label: 'Message — placeholder', kind: 'text', bilingual: true },
      { key: 'submit', label: 'Submit button', kind: 'text', bilingual: true, required: true },
      { key: 'sending', label: 'Sending state', kind: 'text', bilingual: true, required: true },
      {
        key: 'success',
        label: 'Success message',
        kind: 'textarea',
        bilingual: true,
        required: true,
      },
      {
        key: 'error',
        label: 'Error message',
        kind: 'textarea',
        bilingual: true,
        required: true,
        help: 'Shown when the message could NOT be stored. Never show success here.',
      },
    ],
    settingsFields: [],
  },
};

/** True when this admin build knows how to render a form for `type`. */
export function isKnownBlockType(type: string): type is BlockType {
  return Object.prototype.hasOwnProperty.call(BLOCK_SCHEMAS, type);
}

export function blockSchemaFor(type: string): BlockSchema | null {
  // `?? null` rather than a plain index: `noUncheckedIndexedAccess` widens
  // every Record lookup to `| undefined` even after the type guard.
  return isKnownBlockType(type) ? (BLOCK_SCHEMAS[type] ?? null) : null;
}

/** Human label for a type, falling back to the raw key for unknown types. */
export function blockLabel(type: string): string {
  return blockSchemaFor(type)?.label ?? type;
}

/** Gallery order — the order block types are offered in "Add block". */
export const BLOCK_GALLERY: BlockType[] = [...BLOCK_TYPES];

// ─── Empty payloads ─────────────────────────────────────────────────────────
//
// A newly added block POSTs a payload that is structurally right but textually
// blank: the arrays are at their minimum arity and the neutral values carry
// their Zod defaults, so the editor opens on a form with the correct number of
// rows rather than an empty shell the editor has to guess the shape of.
//
// These deliberately do NOT satisfy the API's `min(1)` string rules — a brand
// new block is expected to be invalid until someone types into it. The block
// editor surfaces that as unsaved-until-valid rather than pretending
// "PLACEHOLDER" is content, which is how placeholder copy ends up live.

function blankCta(variant = 'primary'): Record<string, unknown> {
  return { label: '', href: '#', variant };
}

export function emptyContentFor(type: BlockType): Record<string, unknown> {
  switch (type) {
    case 'contact_form':
      return {
        title: '',
        name_label: '',
        email_label: '',
        phone_label: '',
        topic_label: '',
        topic_options: [{ value: 'support', label: '' }],
        message_label: '',
        submit: '',
        sending: '',
        success: '',
        error: '',
      };
    case 'hero_split':
      return { title: '', subtitle: '', ctas: [blankCta()] };
    case 'stats_trio':
      return {
        stats: [
          { value: '', label: '' },
          { value: '', label: '' },
          { value: '', label: '' },
        ],
      };
    case 'dual_value_cards':
      return {
        cards: [
          { title: '', body: '', bullets: [], cta: blankCta(), accent: 'burgundy' },
          { title: '', body: '', bullets: [], cta: blankCta('secondary'), accent: 'sun' },
        ],
      };
    case 'scrollable_grid_with_character':
      return {
        title: '',
        items: [{ glyph: '' }, { glyph: '' }, { glyph: '' }, { glyph: '' }],
      };
    case 'icon_card_grid':
      return {
        title: '',
        cards: [
          { icon: 'sparkle', title: '', body: '' },
          { icon: 'star', title: '', body: '' },
        ],
        columns: 3,
        tone: 'cream',
      };
    case 'pricing_tiers':
      return {
        title: '',
        currency: '',
        tiers: [
          {
            name: '',
            price_display: '',
            period: '',
            features: [],
            cta: blankCta(),
            highlighted: false,
          },
        ],
      };
    case 'testimonial_pair':
      return {
        items: [
          { quote: '', author: '' },
          { quote: '', author: '' },
        ],
      };
    case 'accordion_faq':
      return { title: '', items: [{ question: '', answer: '' }] };
    case 'footer_cta_newsletter':
      return { title: '' };
    case 'rich_text':
      return { body: '', width: 'prose' };
    default:
      // Unreachable for the ten known types; keeps this total if the DB enum
      // gains a value before this map does.
      return {};
  }
}
